import { useCallback, useEffect } from 'react';

import LogoHorizontal from '@assets/logo-horizontal.png';
import { Button } from '@components/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@components/ui/form';
import { Input } from '@components/ui/input';
import { ScrollArea } from '@components/ui/scroll-area';
import { pages } from '@constants/pages';
import { useAuth } from '@contexts/auth';
import errorsValidation from '@errors/validation';
import { zodResolver } from '@hookform/resolvers/zod';
import { type ICreateSessionInputData } from '@services/sessions/dtos/createSession/InputData';
import { type ICreateSessionOutputData } from '@services/sessions/dtos/createSession/OutputData';
import SessionService from '@services/sessions/SessionService';
import { useMutation } from '@tanstack/react-query';
import { LoaderCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';

const formSchema = z.object({
  email: z.email(errorsValidation.email).min(1, {
    message: errorsValidation.required,
  }),
  password: z.string().min(1, {
    message: errorsValidation.required,
  }),
});

function SignIn() {
  const navigate = useNavigate();
  const { authorization, onSetAuthorization } = useAuth();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const { isPending, mutateAsync } = useMutation<
    ICreateSessionOutputData,
    unknown,
    ICreateSessionInputData
  >({
    mutationFn: SessionService.create,
    onError: () => {
      toast.error('Atenção', {
        description: 'Não foi possível fazer o login. Tente novamente',
      });
    },
    onSuccess: ({ token }) => {
      onSetAuthorization({ token });
      navigate(pages.home, { replace: true });
    },
  });

  const handleSubmit = useCallback(
    async (values: z.infer<typeof formSchema>) => {
      await mutateAsync({
        email: values.email,
        senha: values.password,
      });
    },
    [mutateAsync],
  );

  useEffect(() => {
    if (authorization.token) {
      navigate(pages.home, { replace: true });
    }
  }, [authorization.token, navigate]);

  return (
    <ScrollArea className="bg-primary h-dvh">
      <main className="flex min-h-dvh w-full flex-col items-center justify-center p-4">
        <Card className="bg-primary border-background w-full max-w-md">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)}>
              <CardHeader>
                <img
                  alt="Logo GPMED"
                  className="mx-auto mb-8 w-3/4"
                  src={LogoHorizontal}
                />
                <CardTitle className="text-background text-3xl font-semibold">
                  Entrar
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex flex-row justify-between">
                          <FormLabel className="text-background">
                            E-mail
                          </FormLabel>
                          <FormMessage />
                        </div>
                        <FormControl>
                          <Input
                            autoCapitalize="off"
                            className="[&_input]:border-background [&_input]:bg-primary [&_input]:text-background"
                            {...field}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex flex-row justify-between">
                          <FormLabel className="text-background">
                            Senha
                          </FormLabel>
                          <FormMessage />
                        </div>
                        <FormControl>
                          <Input
                            className="[&_input]:border-background [&_input]:bg-primary [&_input]:text-background [&_button]:text-background"
                            secure
                            type="password"
                            {...field}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
                <Link
                  className="text-background mt-4 block text-right text-sm font-light underline"
                  to={pages.auth.forgotPassword}>
                  Esqueci minha senha
                </Link>
              </CardContent>
              <CardFooter className="grid">
                <Button
                  className="cursor-pointer"
                  disabled={isPending}
                  type="submit"
                  variant="secondary">
                  {isPending ? (
                    <LoaderCircle className="text-background animate-spin" />
                  ) : (
                    'ENTRAR'
                  )}
                </Button>
                <Link
                  className="text-background mt-4 block text-center text-sm font-light underline"
                  to={pages.auth.signUp}>
                  Não possui uma conta? Crie uma agora!
                </Link>
              </CardFooter>
            </form>
          </Form>
        </Card>
      </main>
    </ScrollArea>
  );
}

export default SignIn;
